import React, { Component } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined';
import { toast } from 'react-toastify';

const authHeaders = () => {
  const user = JSON.parse(sessionStorage.getItem('user') || 'null');
  const token =
    user &&
    user.signInUserSession &&
    user.signInUserSession.idToken &&
    user.signInUserSession.idToken.jwtToken;
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token || ''}`,
  };
};

const PANEL = {
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  height: '100%',
};

const HEADER = {
  px: 2,
  py: 1.75,
  borderBottom: '1px solid #f1f5f9',
  background: 'linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)',
};

/**
 * DI-6385 — Manage patient-facing Curbside parking spots for a Curbside box.
 * DI-6495 — Custom pick up message for pharmacies without numbered spaces.
 * Distinct from pharmacy storage locations (counter_locations).
 */
class CurbsideParkingSpotsPanel extends Component {
  state = {
    spots: [],
    loading: false,
    saving: false,
    messageSaving: false,
    dialogOpen: false,
    deleteDialogOpen: false,
    editingSpot: null,
    formLabel: '',
    formSortOrder: '',
    useCustomPickupMessage: false,
    customPickupMessage: '',
    savedUseCustomPickupMessage: false,
    savedCustomPickupMessage: '',
  };

  componentDidMount() {
    this.loadSpots();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.boxId !== this.props.boxId) {
      this.loadSpots();
    }
  }

  loadSpots = async () => {
    const { boxId, t } = this.props;
    if (!boxId) {
      this.setState({
        spots: [],
        useCustomPickupMessage: false,
        customPickupMessage: '',
        savedUseCustomPickupMessage: false,
        savedCustomPickupMessage: '',
      });
      return;
    }

    this.setState({ loading: true });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}deliver/curbside-parking-spots/list`,
        {
          method: 'POST',
          withCredentials: true,
          headers: authHeaders(),
          body: JSON.stringify({ box_id: boxId, include_inactive: false }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(
          (result && result.message) ||
            t('BinMapping.ParkingSpots.LoadError', { defaultValue: 'Failed to load parking spots' })
        );
      }
      const data = result.data || {};
      const spots = data.parking_spots || result.parking_spots || [];
      const useCustom = !!data.use_custom_pickup_message;
      const message = data.custom_pickup_message ? String(data.custom_pickup_message) : '';
      this.setState({
        spots,
        loading: false,
        useCustomPickupMessage: useCustom,
        customPickupMessage: message,
        savedUseCustomPickupMessage: useCustom,
        savedCustomPickupMessage: message,
      });
    } catch (error) {
      console.error(error);
      this.setState({ loading: false, spots: [] });
      toast.error(error.message || 'Failed to load parking spots');
    }
  };

  openCreateDialog = () => {
    const nextOrder =
      this.state.spots.length > 0
        ? Math.max(...this.state.spots.map((s) => Number(s.sort_order) || 0)) + 1
        : 0;
    this.setState({
      dialogOpen: true,
      editingSpot: null,
      formLabel: `Space ${nextOrder + 1}`,
      formSortOrder: String(nextOrder),
    });
  };

  openEditDialog = (spot) => {
    this.setState({
      dialogOpen: true,
      editingSpot: spot,
      formLabel: spot.label || '',
      formSortOrder:
        spot.sort_order === 0 || spot.sort_order
          ? String(spot.sort_order)
          : '0',
    });
  };

  closeDialog = () => {
    if (this.state.saving) return;
    this.setState({
      dialogOpen: false,
      editingSpot: null,
      formLabel: '',
      formSortOrder: '',
    });
  };

  openDeleteDialog = (spot) => {
    this.setState({ deleteDialogOpen: true, editingSpot: spot });
  };

  closeDeleteDialog = () => {
    if (this.state.saving) return;
    this.setState({ deleteDialogOpen: false, editingSpot: null });
  };

  handleSave = async () => {
    const { boxId, t } = this.props;
    const { editingSpot, formLabel, formSortOrder } = this.state;
    const label = String(formLabel || '').trim();
    if (!label) {
      toast.error(
        t('BinMapping.ParkingSpots.LabelRequired', { defaultValue: 'Label is required' })
      );
      return;
    }

    const sortOrder = parseInt(formSortOrder, 10);
    this.setState({ saving: true });
    try {
      const isEdit = !!(editingSpot && editingSpot.id);
      const url = isEdit
        ? `${process.env.REACT_APP_BASEURL}deliver/curbside-parking-spots/update`
        : `${process.env.REACT_APP_BASEURL}deliver/curbside-parking-spots`;
      const body = isEdit
        ? {
            id: editingSpot.id,
            box_id: boxId,
            label,
            sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
          }
        : {
            box_id: boxId,
            label,
            sort_order: Number.isFinite(sortOrder) ? sortOrder : undefined,
          };

      const response = await fetch(url, {
        method: 'POST',
        withCredentials: true,
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(
          (result && result.message) ||
            t('BinMapping.ParkingSpots.SaveError', { defaultValue: 'Failed to save parking spot' })
        );
      }
      toast.success(
        isEdit
          ? t('BinMapping.ParkingSpots.Updated', { defaultValue: 'Parking spot updated' })
          : t('BinMapping.ParkingSpots.Created', { defaultValue: 'Parking spot created' })
      );
      this.setState({
        saving: false,
        dialogOpen: false,
        editingSpot: null,
        formLabel: '',
        formSortOrder: '',
      });
      this.loadSpots();
    } catch (error) {
      console.error(error);
      this.setState({ saving: false });
      toast.error(error.message || 'Failed to save parking spot');
    }
  };

  handleDeactivate = async () => {
    const { boxId, t } = this.props;
    const { editingSpot } = this.state;
    if (!editingSpot || !editingSpot.id) return;

    this.setState({ saving: true });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}deliver/curbside-parking-spots/delete`,
        {
          method: 'POST',
          withCredentials: true,
          headers: authHeaders(),
          body: JSON.stringify({
            id: editingSpot.id,
            box_id: boxId,
            hard_delete: false,
          }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(
          (result && result.message) ||
            t('BinMapping.ParkingSpots.DeleteError', { defaultValue: 'Failed to remove parking spot' })
        );
      }
      toast.success(
        t('BinMapping.ParkingSpots.Deactivated', { defaultValue: 'Parking spot removed' })
      );
      this.setState({
        saving: false,
        deleteDialogOpen: false,
        editingSpot: null,
      });
      this.loadSpots();
    } catch (error) {
      console.error(error);
      this.setState({ saving: false });
      toast.error(error.message || 'Failed to remove parking spot');
    }
  };

  handleCustomMessageToggle = (event) => {
    const checked = event.target.checked;
    this.setState({
      useCustomPickupMessage: checked,
      customPickupMessage: checked ? this.state.customPickupMessage : '',
    });
  };

  handleSaveCustomMessage = async () => {
    const { boxId, t } = this.props;
    const { useCustomPickupMessage, customPickupMessage } = this.state;
    const trimmed = String(customPickupMessage || '').trim();

    if (useCustomPickupMessage && !trimmed) {
      toast.error(
        t('BinMapping.ParkingSpots.CustomMessageRequired', {
          defaultValue: 'Enter a custom pick up message, or turn the checkbox off.',
        })
      );
      return;
    }
    if (trimmed.length > 240) {
      toast.error(
        t('BinMapping.ParkingSpots.CustomMessageTooLong', {
          defaultValue: 'Custom pick up message must be 240 characters or fewer.',
        })
      );
      return;
    }

    this.setState({ messageSaving: true });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}deliver/curbside-custom-pickup-message`,
        {
          method: 'POST',
          withCredentials: true,
          headers: authHeaders(),
          body: JSON.stringify({
            box_id: boxId,
            use_custom_pickup_message: useCustomPickupMessage,
            custom_pickup_message: useCustomPickupMessage ? trimmed : null,
          }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(
          (result && result.message) ||
            t('BinMapping.ParkingSpots.CustomMessageSaveError', {
              defaultValue: 'Failed to save custom pick up message',
            })
        );
      }
      const data = result.data || {};
      const useCustom = !!data.use_custom_pickup_message;
      const message = data.custom_pickup_message ? String(data.custom_pickup_message) : '';
      this.setState({
        messageSaving: false,
        useCustomPickupMessage: useCustom,
        customPickupMessage: message,
        savedUseCustomPickupMessage: useCustom,
        savedCustomPickupMessage: message,
      });
      toast.success(
        t('BinMapping.ParkingSpots.CustomMessageSaved', {
          defaultValue: 'Custom pick up message saved',
        })
      );
    } catch (error) {
      console.error(error);
      this.setState({ messageSaving: false });
      toast.error(error.message || 'Failed to save custom pick up message');
    }
  };

  render() {
    const { t, boxName, boxId } = this.props;
    const {
      spots,
      loading,
      saving,
      messageSaving,
      dialogOpen,
      deleteDialogOpen,
      editingSpot,
      formLabel,
      formSortOrder,
      useCustomPickupMessage,
      customPickupMessage,
      savedUseCustomPickupMessage,
      savedCustomPickupMessage,
    } = this.state;

    const customMessageDirty =
      useCustomPickupMessage !== savedUseCustomPickupMessage ||
      String(customPickupMessage || '') !== String(savedCustomPickupMessage || '');

    const helpText = t('BinMapping.ParkingSpots.HelpShort', {
      defaultValue: 'Patient-facing labels for arrival (Space 1, Handicap…). Separate from pharmacy shelves.',
    });

    return (
      <Box sx={PANEL} component="section" aria-labelledby="curbside-parking-heading">
        <Box sx={HEADER}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(14, 116, 144, 0.1)',
                  color: '#0e7490',
                }}
                aria-hidden
              >
                <LocalParkingOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography
                    id="curbside-parking-heading"
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3, fontSize: '1rem' }}
                  >
                    {t('BinMapping.ParkingSpots.Title', { defaultValue: 'Parking Spots' })}
                  </Typography>
                  <Chip
                    size="small"
                    label={spots.length}
                    sx={{
                      height: 22,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      backgroundColor: '#ecfeff',
                      color: '#0e7490',
                      border: '1px solid #a5f3fc',
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: '#64748b', mt: 0.35, fontSize: '0.8125rem', lineHeight: 1.45 }}
                >
                  {helpText}
                </Typography>
                {boxName ? (
                  <Typography
                    variant="caption"
                    sx={{ color: '#94a3b8', display: 'block', mt: 0.35, fontSize: '0.75rem' }}
                  >
                    {boxName}
                  </Typography>
                ) : null}
              </Box>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={this.openCreateDialog}
              disabled={!this.props.boxId || loading}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.8125rem',
                minHeight: 36,
                px: 1.5,
                flexShrink: 0,
                backgroundColor: '#0e7490',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#155e75', boxShadow: 'none' },
                '&:disabled': { backgroundColor: '#cbd5e1', color: '#94a3b8' },
              }}
            >
              {t('BinMapping.ParkingSpots.AddShort', { defaultValue: 'Add spot' })}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            maxHeight: { xs: 280, md: 'none' },
            overflowY: 'auto',
            px: 1,
            py: 1,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
              <CircularProgress size={28} sx={{ color: '#0e7490' }} />
            </Box>
          ) : spots.length === 0 ? (
            <Box
              sx={{
                m: 1,
                p: 2.5,
                borderRadius: '10px',
                border: '1px dashed #cbd5e1',
                backgroundColor: '#f8fafc',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.5 }}>
                {t('BinMapping.ParkingSpots.Empty', {
                  defaultValue:
                    'No parking spots yet. Add Space 1, Space 2, etc. so patients can select where they are parked.',
                })}
              </Typography>
            </Box>
          ) : (
            <Box
              component="ul"
              sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}
            >
              {spots.map((spot) => (
                <Box
                  component="li"
                  key={spot.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 44,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '8px',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { backgroundColor: '#f8fafc' },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '7px',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: '#0e7490',
                      backgroundColor: '#ecfeff',
                      border: '1px solid #cffafe',
                    }}
                    aria-hidden
                  >
                    {spot.sort_order}
                  </Box>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#0f172a',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={spot.label}
                  >
                    {spot.label}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Tooltip title={t('Common.Edit', { defaultValue: 'Edit' })}>
                      <IconButton
                        size="small"
                        onClick={() => this.openEditDialog(spot)}
                        aria-label={`Edit parking spot ${spot.label}`}
                        sx={{
                          width: 36,
                          height: 36,
                          color: '#64748b',
                          '&:hover': { color: '#0e7490', backgroundColor: 'rgba(14, 116, 144, 0.08)' },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Common.Delete', { defaultValue: 'Remove' })}>
                      <IconButton
                        size="small"
                        onClick={() => this.openDeleteDialog(spot)}
                        aria-label={`Remove parking spot ${spot.label}`}
                        sx={{
                          width: 36,
                          height: 36,
                          color: '#64748b',
                          '&:hover': { color: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.08)' },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mx: 2,
            mb: 2,
            mt: 1,
            p: 1.75,
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={useCustomPickupMessage}
                onChange={this.handleCustomMessageToggle}
                disabled={loading || messageSaving || !boxId}
                sx={{
                  color: '#0e7490',
                  '&.Mui-checked': { color: '#0e7490' },
                }}
              />
            }
            label={
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#0f172a' }}>
                {t('BinMapping.ParkingSpots.CustomMessageTitle', {
                  defaultValue: 'Custom pick up message',
                })}
              </Typography>
            }
          />
          <Typography
            variant="body2"
            sx={{ color: '#64748b', fontSize: '0.8125rem', lineHeight: 1.5, mt: 0.5, mb: 1.25 }}
          >
            {t('BinMapping.ParkingSpots.CustomMessageHelp', {
              defaultValue:
                'No dedicated parking spots, no problem. Use this short message to communicate directly to the patient where to park when they arrive. This will show on email and SMS link.',
            })}
          </Typography>
          {useCustomPickupMessage ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <TextField
                value={customPickupMessage}
                onChange={(e) => this.setState({ customPickupMessage: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                disabled={messageSaving}
                placeholder={t('BinMapping.ParkingSpots.CustomMessagePlaceholder', {
                  defaultValue: 'e.g. Park in the driveway near the pharmacy entrance.',
                })}
                inputProps={{ maxLength: 240 }}
                helperText={`${String(customPickupMessage || '').length}/240`}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={this.handleSaveCustomMessage}
                  disabled={messageSaving || !customMessageDirty}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#0e7490',
                    '&:hover': { backgroundColor: '#155e75' },
                  }}
                >
                  {messageSaving ? (
                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                  ) : (
                    t('Common.Save', { defaultValue: 'Save' })
                  )}
                </Button>
              </Box>
            </Box>
          ) : customMessageDirty ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={this.handleSaveCustomMessage}
                disabled={messageSaving}
                sx={{ textTransform: 'none', borderColor: '#0e7490', color: '#0e7490' }}
              >
                {messageSaving ? (
                  <CircularProgress size={18} />
                ) : (
                  t('BinMapping.ParkingSpots.CustomMessageClearSave', {
                    defaultValue: 'Save (turn off message)',
                  })
                )}
              </Button>
            </Box>
          ) : null}
        </Box>

        <Dialog open={dialogOpen} onClose={this.closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
            {editingSpot
              ? t('BinMapping.ParkingSpots.EditTitle', { defaultValue: 'Edit Parking Spot' })
              : t('BinMapping.ParkingSpots.AddTitle', { defaultValue: 'Add Parking Spot' })}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label={t('BinMapping.ParkingSpots.Label', { defaultValue: 'Label' })}
                value={formLabel}
                onChange={(e) => this.setState({ formLabel: e.target.value })}
                fullWidth
                placeholder="Space 1"
                autoFocus
              />
              <TextField
                label={t('BinMapping.ParkingSpots.SortOrder', { defaultValue: 'Display order' })}
                value={formSortOrder}
                onChange={(e) => this.setState({ formSortOrder: e.target.value })}
                fullWidth
                type="number"
                helperText={
                  t('BinMapping.ParkingSpots.SortOrderHelp', {
                    defaultValue: 'Lower numbers appear first for the patient.',
                  })
                }
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={this.closeDialog} disabled={saving} sx={{ textTransform: 'none' }}>
              {t('Common.Cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              onClick={this.handleSave}
              variant="contained"
              disabled={saving}
              sx={{
                textTransform: 'none',
                backgroundColor: '#0e7490',
                '&:hover': { backgroundColor: '#155e75' },
              }}
            >
              {saving ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                t('Common.Save', { defaultValue: 'Save' })
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={this.closeDeleteDialog}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t('BinMapping.ParkingSpots.RemoveTitle', { defaultValue: 'Remove parking spot?' })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('BinMapping.ParkingSpots.RemoveMessage', {
                defaultValue:
                  'Patients will no longer see this spot. Existing orders are unaffected.',
              })}
              {editingSpot ? (
                <Box
                  component="span"
                  sx={{ display: 'block', mt: 1.5, fontWeight: 600, color: '#0f172a' }}
                >
                  {editingSpot.label}
                </Box>
              ) : null}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={this.closeDeleteDialog} disabled={saving} sx={{ textTransform: 'none' }}>
              {t('Common.Cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              onClick={this.handleDeactivate}
              color="error"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              {saving ? <CircularProgress size={18} /> : t('Common.Delete', { defaultValue: 'Remove' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default CurbsideParkingSpotsPanel;



// WEBPACK FOOTER //
// ./src/components/bin-mapping/CurbsideParkingSpotsPanel.js